import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import PinLegendCreationVisibility from "@mapmycustomers/shared/enum/PinLegendCreationVisibility";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import UserLocationSettingVisibility from "@app/enum/UserLocationSettingVisibility";
import MapSettings from "@app/scene/settings/type/MapSettings";
import {
  getOrganizationId,
  getOrganizationSettings,
  getPinLegendCreationVisibility,
} from "@app/store/iam";
import { updateOrganization, updateOrganizationMetadata } from "@app/store/iam/actions";
import { getDefaultColorKeys, getDefaultShapeKeys } from "@app/store/pinLegends";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import useSetting from "@app/util/hook/useSetting";

import CreationVisibility from "./components/CreationVisibility";
import DefaultKeys from "./components/DefaultKeys";
import UserLocationVisibility from "./components/UserLocationVisibility";
import styles from "./Map.module.scss";

interface Props {
  defaultColorKey: Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>>;
  defaultShapeKey: Partial<Record<EntityTypesSupportedByMapsPage, PinLegend["id"]>>;
  form: FormInstance<MapSettings>;
  onUpdateOrganization: typeof updateOrganization.request;
  onUpdateOrganizationMetadata: typeof updateOrganizationMetadata.request;
  organizationId?: Organization["id"];
  organizationSettings: Organization["settings"];
  pinLegendsCreation: PinLegendCreationVisibility;
  setDirty: () => void;
  unsetDirty: () => void;
}

const Map: React.FC<Props> = ({
  defaultColorKey,
  defaultShapeKey,
  form,
  onUpdateOrganization,
  onUpdateOrganizationMetadata,
  organizationId,
  organizationSettings,
  pinLegendsCreation,
  setDirty,
  unsetDirty,
}) => {
  const intl = useIntl();
  const handleSetDirty = useCallback(() => {
    setDirty();
  }, [setDirty]);

  const userLocationVisibilitySetting = useSetting(
    organizationSettings,
    OrganizationSetting.USER_LOCATION_VISIBILITY
  );

  const handleSubmit = useCallback(
    async (values: MapSettings) => {
      const { userLocationVisibility, ...metaData } = values;
      await Promise.all([
        new Promise<void>((resolve) => {
          onUpdateOrganizationMetadata({
            callback: () => resolve(),
            metaData,
          });
        }),
        ...(organizationId
          ? [
              new Promise<void>((resolve) => {
                onUpdateOrganization({
                  callback: () => resolve(),
                  organization: { id: organizationId },
                  settings: [
                    userLocationVisibilitySetting && userLocationVisibility
                      ? { ...userLocationVisibilitySetting, value: userLocationVisibility }
                      : undefined,
                  ].filter(isDefined),
                });
              }),
            ]
          : []),
      ]);
      unsetDirty();
    },
    [
      organizationId,
      onUpdateOrganizationMetadata,
      onUpdateOrganization,
      userLocationVisibilitySetting,
      unsetDirty,
    ]
  );

  return (
    <ExtendedAnalytics keyPath={["Activities"]}>
      <Form
        form={form}
        initialValues={{
          defaultColorKey,
          defaultShapeKey,
          pinLegendsCreation,
          userLocationVisibility:
            userLocationVisibilitySetting?.value ?? UserLocationSettingVisibility.OWNER,
        }}
        layout="vertical"
        onFieldsChange={handleSetDirty}
        onFinish={handleSubmit}
      >
        <Row gutter={[0, layout.spacerL]}>
          <Col sm={{ span: 24 }} xl={{ span: 12 }}>
            <Form.Item
              label={intl.formatMessage({
                id: "settings.organizationDetails.map.creationVisibility",
                defaultMessage: "Allow creation of custom Pin Legends by",
                description:
                  "Pin Legend creation visibility field label in Org Details settings page",
              })}
              name="pinLegendsCreation"
            >
              <CreationVisibility className={styles.visibility} />
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: "settings.organizationDetails.map.userLocationVisibility",
                defaultMessage: "Allow view of User Location layer by",
                description:
                  "Pin Legend creation visibility field label in Org Details settings page",
              })}
              name="userLocationVisibility"
            >
              <UserLocationVisibility className={styles.visibility} />
            </Form.Item>
            <Form.Item
              label={intl.formatMessage({
                id: "settings.organizationDetails.map.defaultKeys",
                defaultMessage: "Legend Defaults",
                description: "Legend defaults field label in Org Details settings page",
              })}
            >
              <DefaultKeys />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  defaultColorKey: getDefaultColorKeys(state),
  defaultShapeKey: getDefaultShapeKeys(state),
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
  pinLegendsCreation: getPinLegendCreationVisibility(state),
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
  onUpdateOrganizationMetadata: updateOrganizationMetadata.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);
